

import React, { useRef, useEffect, useState } from 'react';
import { Input } from '@/src/components/ui/shadcn/input';
import { Search } from 'lucide-react';
import { UserAvatar } from '../user-avatar';
import '../../styles/animations.css';

interface SearchInputProps {
  className?: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isFocused: boolean;
  onFocusChange: (focused: boolean) => void;
  onSearchClick?: (e: React.MouseEvent) => void;
  onBackdropClick?: (e: React.MouseEvent) => void;
  showBackdrop?: boolean;
  lockBodyScroll?: boolean;
}



export const SearchInput: React.FC<SearchInputProps> = ({
  className = '',
  searchQuery,
  onSearchChange,
  isFocused,
  onFocusChange,
  onSearchClick,
  onBackdropClick,
  showBackdrop = true,
  lockBodyScroll = true,
}) => {
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showingBackdrop, setShowingBackdrop] = useState(false);




  useEffect(() => {
    if (isFocused) {
      setShowingBackdrop(true);
    } else {
      const timer = setTimeout(() => {
        setShowingBackdrop(false);
      }, 200); // Match fadeOut animation duration
      return () => clearTimeout(timer);
    }
  }, [isFocused]);





  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onFocusChange(true);
        inputRef.current?.focus();
      } else if (e.key === 'Escape' && isFocused) {
        e.preventDefault();
        onFocusChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFocused, onFocusChange]);


  useEffect(() => {
    if (!lockBodyScroll) return;

    if (isFocused) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFocused, lockBodyScroll]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSearchChange(value);
  };

  return (
    <div
      className={`relative flex justify-center items-center gap-3 ${className}`}
      ref={searchRef}
      onClick={onSearchClick}
    >
      <div className="relative w-[400px] transition-all duration-300 z-50">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="search"
          placeholder="Find campaigns and resources..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full pl-9 text-sm font-medium rounded-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#6366f1] focus:ring-opacity-50 focus:shadow-[0_0_10px_rgba(99,102,241,0.3)] dark:focus:[background:linear-gradient(#1f2937,#1f2937)_padding-box,linear-gradient(45deg,#6366f1,#818cf8)_border-box] transition-colors"
          onFocus={() => onFocusChange(true)}
          onClick={(e) => {
            e.stopPropagation();
            onSearchClick?.(e);
          }}
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border 
                     border-gray-200 bg-gray-100 px-1.5  text-[10px] font-medium
                     text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          ⌘K
        </kbd>
      </div>

      <div data-testid="spotlight-user-avatar-container" onClick={(e) => e.stopPropagation()}>
        <UserAvatar />
      </div>

      {showBackdrop && showingBackdrop && (
        <div
          className="fixed inset-0 bg-black/50 z-[90] transition-opacity duration-200 ease-out"
          style={{
            animation: isFocused ? 'fadeIn 200ms forwards' : 'fadeOut 200ms forwards',
            willChange: 'opacity',
            transform: 'translateZ(0)',
          }}
          onClick={onBackdropClick}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
